import React, { ForwardedRef, forwardRef, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ButtonIcon } from '@components/button';
import { ActionItemButton } from '@components/button/src/action-button';
import { ButtonIconProps } from '@components/button/src/button.types';
import { BUTTON_VARIANT_CLEAN, BUTTON_VARIANT_S } from '@components/button/src/config';
import { DragHandle } from '@components/drag-handle';
import { IconDotsVertical } from '@components/icons';
import { useDragToClose } from '@components/modals/modal-v2/src/use-drag-to-close';
import { MD_VIEWPORT } from '@constants/viewport';
import {
  Dialog,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { useIsTouchDevice } from '@hooks/use-is-touch-device';
import { useWindowSize } from '@hooks/use-window-size';
import classNames from 'classnames';

/** Check if the touchend point is still within the element that received touchstart. */
const isTouchInsideTarget = (e: React.TouchEvent): boolean => {
  const touch = e.changedTouches[0];
  const rect = e.currentTarget.getBoundingClientRect();
  return (
    touch.clientX >= rect.left &&
    touch.clientX <= rect.right &&
    touch.clientY >= rect.top &&
    touch.clientY <= rect.bottom
  );
};

export interface SubmenuAction {
  name: string;
  disabled?: boolean;
}

export interface ButtonAction extends SubmenuAction {
  active?: boolean;
  action?: (e?: React.MouseEvent<HTMLElement>) => void;
  close?: () => void;
  icon?: React.ReactNode;
}
export interface Actions {
  name: string;
  disabled?: boolean;
  active?: boolean;
  link?: string;
  action?: (e?: React.MouseEvent<HTMLElement>) => void;
  icon?: React.ReactNode;
}

interface LinkAction extends SubmenuAction {
  active?: boolean;
  link: string;
  close?: () => void;
  icon?: React.ReactNode;
}

export const SubmenuIconLink = forwardRef<HTMLAnchorElement, LinkAction>(
  ({ name, disabled, link, active, close, icon }: LinkAction, ref) => {
    const navigate = useNavigate();
    const executeAction = () => {
      if (close) close();
      navigate(link);
    };
    return (
      <Link
        ref={ref}
        key={`action-menu-${name}`}
        id='bottom-sheet'
        className={classNames(
          'flex items-center rounded-lg px-6 xs:px-4 py-4 xs:py-2 text-headline-xs xs:text-subtitle-m w-full gap-3 text-left text-primary',
          {
            'fill-secondary': !active,
            'fill-secondary-active': active,
            'cursor-pointer-none pointer-events-none fill-secondary-disabled text-transparent-20': disabled,
          }
        )}
        to={link}
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
          e.preventDefault();
          e.stopPropagation();
          executeAction();
        }}
        onTouchEnd={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (isTouchInsideTarget(e)) executeAction();
        }}>
        {icon && <span className='h-5 w-5 flex items-center justify-center'>{icon}</span>}
        {name}
      </Link>
    );
  }
);

export const SubmenuIconButton = forwardRef(
  ({ name, disabled, action, active, close, icon }: ButtonAction, ref: ForwardedRef<HTMLButtonElement>) => {
    const executeAction = () => {
      if (close) close();
      if (action) action();
    };
    const handleClick = (e?: React.MouseEvent<HTMLElement>) => {
      if (e) e.stopPropagation();
      executeAction();
    };
    const handleTouchEnd = (e: React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (isTouchInsideTarget(e)) executeAction();
    };
    return (
      <ActionItemButton
        ref={ref}
        key={`option-menu-${name}`}
        active={active ?? false}
        onClick={handleClick}
        onTouchEnd={handleTouchEnd}
        text={name}
        disabled={disabled}
        icon={icon}
      />
    );
  }
);

const MobileSubmenuIcon = ({
  options,
  buttonContent,
  buttonClasses,
  size,
  variant,
  label,
}: {
  options: Actions[];
  buttonContent?: React.ReactNode;
  buttonClasses?: string;
  dotsClasses?: string;
  size?: ButtonIconProps['size'];
  variant?: ButtonIconProps['variant'];
  label?: string;
}) => {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const { t } = useTranslation();
  const resolvedLabel = label ?? t('common:optionsLabel');

  const closeMobileMenu = () => setShowMobileMenu(false);

  const { panelRef, isClosing, handleTouchStart, handleTouchMove, handleTouchEnd, handleTouchCancel, initiateClose } =
    useDragToClose({ isOpen: showMobileMenu, onClose: closeMobileMenu });

  return (
    <>
      <button
        type='button'
        aria-label={resolvedLabel}
        className={classNames(buttonClasses)}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setShowMobileMenu(!showMobileMenu);
        }}>
        {buttonContent ?? <ButtonIcon as='div' Icon={IconDotsVertical} size={size} variant={variant} />}
      </button>
      <Transition show={showMobileMenu && !isClosing} appear>
        <Dialog
          onClose={() => initiateClose('backdrop')}
          className='relative z-50'
          aria-label={resolvedLabel}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <TransitionChild
            as={Fragment}
            enter='duration-[450ms] ease-[cubic-bezier(0.32,0.72,0,1)]'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='duration-[350ms] ease-[cubic-bezier(0.32,0.72,0,1)]'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'>
            <div className='fixed inset-0 backdrop-overlay' aria-hidden='true' />
          </TransitionChild>
          <TransitionChild
            enter='duration-[450ms] ease-[cubic-bezier(0.32,0.72,0,1)]'
            enterFrom='translate-y-full'
            enterTo='translate-y-0'
            leave='duration-[350ms] ease-[cubic-bezier(0.32,0.72,0,1)]'
            leaveFrom='translate-y-0'
            leaveTo='translate-y-full'>
            <div className='fixed inset-0 flex items-end'>
              <DialogPanel
                ref={panelRef}
                className='flex flex-col gap-3 w-full text-primary bg-primary px-6 xs:px-4 pb-10 pt-0 xs:pb-8 rounded-t-lg shadow-[2px_2px_20px_0_rgba(0,0,0,0.15)] overscroll-contain'
                onClick={(e) => e.stopPropagation()}>
                <DragHandle
                  onClose={initiateClose}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  onTouchCancel={handleTouchCancel}
                />
                <p className='text-left text-body-xs mb-2 text-secondary'>{resolvedLabel}</p>
                {options.map((option) => {
                  return option.link ? (
                    <SubmenuIconLink
                      key={`action-menu-${option.name}`}
                      link={option.link}
                      name={option.name}
                      disabled={option.disabled}
                      icon={option.icon}
                      close={initiateClose}
                    />
                  ) : (
                    <SubmenuIconButton key={`action-menu-${option.name}`} {...option} close={initiateClose} />
                  );
                })}
              </DialogPanel>
            </div>
          </TransitionChild>
        </Dialog>
      </Transition>
    </>
  );
};

export const SubmenuIcon = ({
  options,
  buttonContent,
  className,
  buttonClasses,
  size = BUTTON_VARIANT_S,
  variant = BUTTON_VARIANT_CLEAN,
  zIndexClassName = '',
  label,
  disabled = false,
}: {
  buttonContent?: React.ReactNode;
  options: Actions[];
  className?: string;
  buttonClasses?: string;
  size?: ButtonIconProps['size'];
  variant?: ButtonIconProps['variant'];
  zIndexClassName?: string;
  label?: string;
  disabled?: boolean;
}) => {
  const isMobileViewPort = useWindowSize(MD_VIEWPORT as number);
  const isTouchDevice = useIsTouchDevice();
  const { t } = useTranslation();
  const resolvedLabel = label ?? t('common:optionsLabel');

  // Use the mobile bottom-sheet variant on touch-primary devices (e.g. iPad) as well,
  // since the desktop dropdown nests a <button> inside an <a>/<button> card wrapper,
  // which iPadOS Safari routes to the wrong target on touch (dropdown fails to open).
  const shouldUseMobileMenu = isMobileViewPort || isTouchDevice;

  if (disabled) {
    return (
      <div className={classNames('flex relative', className)} data-testid='submenu'>
        <ButtonIcon
          Icon={IconDotsVertical}
          size={size}
          variant={variant}
          disabled
          aria-label={resolvedLabel}
          className={buttonClasses}
        />
      </div>
    );
  }

  if (shouldUseMobileMenu) {
    return (
      <MobileSubmenuIcon
        options={options}
        buttonContent={buttonContent}
        buttonClasses={buttonClasses}
        size={size}
        variant={variant}
        label={resolvedLabel}
      />
    );
  }
  return (
    <Menu as='div' className={classNames('flex relative', className)} data-testid='submenu'>
      {({ close, open }) => (
        <div className='relative'>
          <MenuButton
            aria-label={resolvedLabel}
            className={classNames(buttonClasses)}
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.preventDefault();
              e.stopPropagation();
            }}>
            {buttonContent ?? <ButtonIcon as='div' Icon={IconDotsVertical} size={size} variant={variant} />}
          </MenuButton>
          <Transition
            show={open}
            as={Fragment}
            appear={true}
            enter='transition ease-out duration-100'
            enterFrom='opacity-0 scale-95'
            enterTo='opacity-100 scale-100'
            leave='transition ease-in duration-75'
            leaveFrom='opacity-100 scale-100'
            leaveTo='opacity-0 scale-95'>
            <MenuItems
              modal={false}
              anchor={{ to: 'bottom end', gap: 0 }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              className={classNames(
                'w-56 flex flex-col gap-2 p-3 bg-primary rounded-lg shadow-[2px_2px_20px_0_rgba(0,0,0,0.25)] overflow-auto focus:outline-hidden',
                zIndexClassName
              )}>
              <p className='text-left text-body-xs mb-2 text-secondary'>{resolvedLabel}</p>
              {options.map((option) => (
                <MenuItem key={option.name}>
                  {({ focus }) =>
                    option.link ? (
                      <SubmenuIconLink
                        key={`action-menu-${option.name}`}
                        {...option}
                        link={option.link}
                        active={focus}
                        close={close}
                      />
                    ) : (
                      <SubmenuIconButton
                        key={`action-menu-${option.name}`}
                        {...option}
                        active={focus || !!option.active}
                        close={close}
                      />
                    )
                  }
                </MenuItem>
              ))}
            </MenuItems>
          </Transition>
        </div>
      )}
    </Menu>
  );
};
